/**
 * 세특 예시 & 작성법 가이드 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 세특 예시, 세특 작성법, 세특 문장, 교과세특, 통합과학 세특
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import '../../styles/design-system/index.css';

const SetakExampleGuidePage = () => {
  const navigate = useNavigate();
  const [selectedSubject, setSelectedSubject] = useState('통합과학');

  const subjects = ['통합과학', '통합사회', '국어', '영어', '수학', '한국사'];

  const examples = {
    '통합과학': [
      {
        activity: '기후변화와 탄소중립 탐구 프로젝트',
        text: '기후변화의 원인과 영향을 과학적으로 분석하고, 탄소중립 실현 방안을 모색하는 프로젝트에 참여함. 온실가스 배출량 데이터를 수집·분석하여 그래프로 시각화하고, 신재생에너지의 원리와 활용 사례를 조사하여 발표함. 특히 태양전지의 작동 원리를 실험을 통해 검증하며 과학적 탐구 능력을 발휘함.'
      },
      {
        activity: '생태계와 환경 보전 실험',
        text: '수질 오염이 생태계에 미치는 영향을 실험적으로 탐구함. 다양한 농도의 오염 물질이 수생 생물에 미치는 영향을 관찰·기록하고, 결과를 과학적으로 분석하여 환경 보전의 중요성을 인식함. 실험 결과를 바탕으로 학교 주변 하천 정화 활동에 자발적으로 참여함.'
      }
    ],
    '통합사회': [
      {
        activity: '민주주의와 시민참여 탐구',
        text: '민주주의의 발전 과정과 시민 참여의 중요성을 탐구함. 역사적 민주화 운동 사례를 분석하고, 현대 사회에서의 시민 참여 방식 변화를 조사하여 발표함. 특히 디지털 시대의 새로운 참여 방식에 대해 비판적으로 고찰하며 사회 문제에 대한 관심과 참여 의지를 보임.'
      }
    ],
    '국어': [
      {
        activity: '문학작품 깊이읽기 활동',
        text: '현대소설의 서사 구조와 인물 분석을 통해 작품의 주제의식을 탐구함. 작가의 시대적 배경과 창작 의도를 연결하여 해석하는 능력이 뛰어남. 모둠 토론에서 자신의 견해를 논리적으로 제시하고, 다른 의견을 경청하며 통합적 사고력을 발휘함.'
      }
    ],
    '영어': [
      {
        activity: '영어 에세이 작성 및 발표',
        text: '사회적 이슈에 대한 영어 에세이를 작성하고 발표함. 논리적인 문장 구성과 적절한 어휘 선택으로 자신의 주장을 효과적으로 전달함. 발표 후 질의응답에서 즉흥적인 영어 대화 능력을 보이며 의사소통 역량을 발휘함.'
      }
    ],
    '수학': [
      {
        activity: '수학적 모델링 프로젝트',
        text: '실생활 문제를 수학적으로 모델링하여 해결하는 프로젝트에 참여함. 인구 변화 데이터를 분석하여 지수함수 모델을 적용하고, 미래 인구를 예측하는 과정에서 수학적 추론 능력을 발휘함. 결과를 시각화하여 발표하며 수학의 실용적 가치를 인식함.'
      }
    ],
    '한국사': [
      {
        activity: '역사 사료 분석 활동',
        text: '일제강점기 독립운동 사료를 분석하고 해석하는 활동에 적극적으로 참여함. 1차 사료와 2차 사료를 비교·분석하여 역사적 사실을 객관적으로 파악하는 능력을 보임. 역사적 인물의 삶과 시대적 배경을 연결하여 깊이 있는 탐구 보고서를 작성함.'
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>세특 예시 모음 | 과목별 세특 작성법·문장 예시 (2026 기재요령 반영)</title>
        <meta name="description" content="통합과학, 통합사회, 국어, 영어, 수학, 한국사 세특 예시 모음. 2026 기재요령 100% 반영. 과목별 세특 작성법과 문장 예시. AI로 나만의 세특 자동 생성." />
        <meta name="keywords" content="세특 예시, 세특 작성법, 세특 문장, 교과세특, 통합과학 세특, 통합사회 세특, 국어 세특, 영어 세특, 수학 세특, 한국사 세특, 세특 예시 모음, 세부능력 및 특기사항" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/setak-example" />
        
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/setak-example" />
        <meta property="og:title" content="세특 예시 모음 | 과목별 작성법·문장 예시 (2026)" />
        <meta property="og:description" content="통합과학, 국어, 영어 등 과목별 세특 예시. 2026 기재요령 반영. AI 자동 생성." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:locale" content="ko_KR" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "세특 예시 모음 - 과목별 세부능력 및 특기사항 작성법",
            "description": "2026년 기재요령에 맞는 과목별 세특 예시와 작성법 가이드",
            "author": { "@type": "Organization", "name": "왓퀴즈" },
            "publisher": { "@type": "Organization", "name": "왓퀴즈" },
            "datePublished": "2026-02-05",
            "dateModified": "2026-02-05"
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#dbeafe',
              color: '#1d4ed8',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              2026 기재요령 반영
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              세특 예시 모음<br />
              <span style={{ color: '#3182f6' }}>과목별 작성법</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              통합과학, 국어, 영어, 수학 등<br />
              <strong>과목별 세특 예시와 작성 가이드</strong>
            </p>
          </div>
        </section>

        {/* 과목 선택 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            과목 선택
          </h2>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            {subjects.map((subject) => (
              <button
                key={subject}
                onClick={() => setSelectedSubject(subject)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: selectedSubject === subject ? '#fff' : '#374151',
                  backgroundColor: selectedSubject === subject ? '#3182f6' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {subject}
              </button>
            ))}
          </div>

          {/* 예시 표시 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#3182f6'
            }}>
              {selectedSubject} 세특 예시
            </h3>

            {examples[selectedSubject]?.map((example, idx) => (
              <div key={idx} style={{
                padding: '24px',
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '12px'
                }}>
                  {example.activity}
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#475569',
                  lineHeight: '1.8',
                  margin: 0,
                  padding: '16px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  {example.text}
                </p>
                <p style={{
                  fontSize: '12px',
                  color: '#94a3b8',
                  marginTop: '8px',
                  textAlign: 'right'
                }}>
                  {example.text.length}자
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 작성 팁 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#f8fafc'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              세특 작성 핵심 팁 5가지
            </h2>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {[
                { num: '1', tip: '구체적 활동 → 학생의 역할 → 배움/성장 순으로 기술' },
                { num: '2', tip: '수치, 사례 등 구체적 근거 포함 (예: 3회 발표, 5편 분석)' },
                { num: '3', tip: '과목 특성에 맞는 역량 키워드 활용' },
                { num: '4', tip: '학생의 변화·성장 과정을 드러내기' },
                { num: '5', tip: '진로와 연계된 활동 및 역량 강조' }
              ].map((item) => (
                <div key={item.num} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  padding: '16px 20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px'
                }}>
                  <span style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#3182f6',
                    color: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>{item.num}</span>
                  <p style={{
                    fontSize: '15px',
                    color: '#374151',
                    margin: 0
                  }}>{item.tip}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              세특 작성,<br />
              <span style={{ color: '#3182f6' }}>AI가 3분 만에</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              과목, 활동, 학생 특성만 입력하면<br />
              기재요령에 맞는 세특 문장을 <strong>자동 생성</strong>해드립니다.
            </p>

            <button
              onClick={() => navigate('/create-saenggibu')}
              style={{
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#3182f6',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              무료로 세특 생성하기
            </button>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/saenggibu-2026" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              2026 생기부 기재요령
            </Link>
            <Link to="/saenggibu-inspector" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              생기부 기재요령 검사기
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default SetakExampleGuidePage;
